import { Loader2 } from "lucide-react";
import type * as React from "react";
import { useState } from "react";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getSession, signIn } from "@/lib/auth-client";

export function LoginPage() {
	const [, setLocation] = useLocation();
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [error, setError] = useState("");
	const [isLoading, setIsLoading] = useState(false);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError("");
		setIsLoading(true);

		try {
			const result = await signIn.email({
				email,
				password,
			});

			if (result.error) {
				setError(result.error.message || "Invalid credentials");
			} else {
				// Wait for session to be established before redirecting
				await new Promise((resolve) => setTimeout(resolve, 100));
				const session = await getSession();
				if (session?.data?.session) {
					setLocation("/");
				} else {
					// Fallback: force a page reload to ensure session is loaded
					window.location.href = "/";
				}
			}
		} catch (err) {
			setError("An error occurred. Please try again.");
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-primary/10 p-4">
			{/* Background decoration */}
			<div className="absolute inset-0 overflow-hidden pointer-events-none">
				<div className="absolute -top-40 -left-20 w-80 h-80 bg-primary/10 rounded-full blur-3xl opacity-50" />
				<div className="absolute top-1/2 -right-40 w-96 h-96 bg-accent/10 rounded-full blur-3xl opacity-50" />
				<div className="absolute -bottom-40 left-1/2 w-80 h-80 bg-primary/20 rounded-full blur-3xl opacity-30" />
			</div>

			<Card className="w-full max-w-md relative z-10 shadow-2xl border-border/50 backdrop-blur-md bg-card/80">
				<CardHeader className="text-center pb-2">
					<div className="mx-auto w-16 h-16 rounded-2xl flex items-center justify-center mb-4 shadow-lg animate-in zoom-in duration-500">
						<img
							src="/emantime-logo.png"
							alt="EmanTime"
							className="w-10 h-10 object-contain"
						/>
					</div>
					<CardTitle className="text-2xl font-bold tracking-tight">
						Welcome Back
					</CardTitle>
					<CardDescription className="text-base text-muted-foreground">
						WhatsApp AI Agent Dashboard
					</CardDescription>
				</CardHeader>
				<CardContent className="pt-4 px-8">
					<form onSubmit={handleSubmit} className="space-y-5">
						<div className="space-y-2">
							<Label htmlFor="email">Email Address</Label>
							<Input
								id="email"
								type="email"
								placeholder="you@example.com"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								required
								disabled={isLoading}
								className="h-11 bg-background/50 border-border/60 focus:ring-2 focus:ring-primary/20 transition-all"
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="password">Password</Label>
							<Input
								id="password"
								type="password"
								placeholder="••••••••"
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								required
								disabled={isLoading}
								className="h-11 bg-background/50 border-border/60 focus:ring-2 focus:ring-primary/20 transition-all"
							/>
						</div>

						{error && (
							<div className="text-sm text-destructive bg-destructive/10 p-3 rounded-lg border border-destructive/20 animate-in fade-in slide-in-from-top-1">
								{error}
							</div>
						)}

						<Button
							type="submit"
							className="w-full h-12 text-base font-semibold shadow-md active:scale-[0.98] transition-all"
							disabled={isLoading}
						>
							{isLoading ? (
								<>
									<Loader2 className="w-5 h-5 mr-3 animate-spin" />
									Signing in...
								</>
							) : (
								"Sign In"
							)}
						</Button>
					</form>
				</CardContent>
				<CardFooter className="pb-8">
					<div className="text-sm text-center w-full text-muted-foreground">
						Don't have an account?{" "}
						<Link
							href="/register"
							className="text-primary font-semibold hover:underline transition-all"
						>
							Create one
						</Link>
					</div>
				</CardFooter>
			</Card>
		</div>
	);
}
